import { polygon, simplify } from '@turf/turf';
import { FieldGeometry } from 'base/types/RecommendationCropwise';
import { SoilPropertiesRequest, SoilPropertiesResponse } from 'base/types/FieldConditions';

const SOIL_REQUEST_BYTE_LIMIT = 7000;
const SIMPLIFY_TOLERANCES = [0, 0.00001, 0.00002, 0.00005, 0.0001, 0.00025, 0.0005, 0.001, 0.002];

export function buildSoilPropertiesRequest(
  geometry: FieldGeometry,
  organizationId: string
): SoilPropertiesRequest {
  if (geometry.type !== 'Polygon') {
    throw new Error('Soil properties require a polygon');
  }
  const field = polygon(geometry.coordinates);
  for (const tolerance of SIMPLIFY_TOLERANCES) {
    const feature = tolerance ? simplify(field, { tolerance, highQuality: true }) : field;
    const request: SoilPropertiesRequest = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: feature.geometry }],
      datasource: 'soilgrid',
      soildepth: '0-5cm',
    };
    const bytes = new TextEncoder().encode(JSON.stringify({ organizationId, ...request })).length;
    if (
      bytes <= SOIL_REQUEST_BYTE_LIMIT &&
      feature.geometry.coordinates.every((ring) => ring.length <= 1250)
    ) {
      return request;
    }
  }
  throw new Error('Field geometry exceeds the soil properties request limit');
}

export function parseSoilClayPercentage(
  soil: SoilPropertiesResponse | null | undefined
): number | null {
  const averages = ['clay', 'silt', 'sand'].map((name) => {
    const data = soil?.variables?.find((entry) => entry.variable?.toLowerCase() === name)?.data;
    if (!data?.length) {
      return null;
    }
    const values = data.map((entry) => (Array.isArray(entry) ? entry[0] : entry));
    if (
      !values.every(
        (value): value is number =>
          typeof value === 'number' && Number.isFinite(value) && value >= 0
      )
    ) {
      return null;
    }
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  });
  const [clay, silt, sand] = averages;
  if (clay === null || silt === null || sand === null || clay + silt + sand <= 0) {
    return null;
  }
  return Math.round((clay / (clay + silt + sand)) * 1000) / 10;
}
